import { NavLink } from 'react-router-dom';

import { Icon } from '@/components/ui';
import { paths } from '@/routes/paths';
import { cn } from '@/utils/cn';

import { BrandPanel } from './BrandPanel';

/**
 * Shared frame for every authentication screen: registration, password
 * reset and email verification. The same split card as the login page --
 * the night-purple brand panel on the left, the form on the right, in the
 * product's violet (`.auth-shell`) -- so every way into the product looks
 * like the same door.
 *
 * `heading` / `points` let a page speak to its own reader in the panel.
 * `accountSwitch` shows the student / company choice above the title on the
 * two registration screens -- links to the existing pages, nothing more.
 */
export function AuthCard({
  title,
  description,
  children,
  footer,
  heading,
  points,
  accountSwitch = false,
}) {
  return (
    <section className="auth-shell bg-surface-muted">
      <div className="app-container flex justify-center py-8 sm:py-14">
        <div className="rounded-panel shadow-card border-line grid w-full max-w-5xl overflow-hidden border lg:grid-cols-[5fr_7fr]">
          <BrandPanel heading={heading} points={points} />

          <div className="bg-surface flex flex-col justify-center p-6 sm:p-10 lg:p-12">
            {accountSwitch && <AccountSwitch />}

            <h1 className="text-ink text-2xl font-semibold tracking-tight">{title}</h1>
            {description && <p className="text-ink-muted mt-2 text-sm">{description}</p>}

            <div className="mt-6">{children}</div>

            {footer && (
              <p className="text-ink-muted border-line mt-6 border-t pt-5 text-center text-sm">
                {footer}
              </p>
            )}
          </div>
        </div>
      </div>
    </section>
  );
}

const ACCOUNT_TYPES = [
  { to: paths.registerStudent, label: "I'm a student", icon: 'graduationCap' },
  { to: paths.registerCompany, label: "I'm hiring", icon: 'building' },
];

/** Which kind of account is being made, as two links styled as a switch. */
function AccountSwitch() {
  return (
    <nav
      aria-label="Account type"
      className="bg-surface-muted border-line mb-6 grid grid-cols-2 gap-1 rounded-xl border p-1"
    >
      {ACCOUNT_TYPES.map((type) => (
        <NavLink
          key={type.to}
          to={type.to}
          className={({ isActive }) =>
            cn(
              'flex items-center justify-center gap-2 rounded-lg px-3 py-2 text-sm font-medium transition',
              isActive ? 'bg-surface text-brand-700 shadow-sm' : 'text-ink-muted hover:text-ink',
            )
          }
        >
          <Icon name={type.icon} size={16} />
          {type.label}
        </NavLink>
      ))}
    </nav>
  );
}
